import logger from '../../lib/logger';
import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const SettingsTab = ({ token, apiBaseUrl }) => {
  const [shahadatEnabled, setShahadatEnabled] = useState(false);
  const [shahadatMessage, setShahadatMessage] = useState('');
  const [heroBanner, setHeroBanner] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/settings`);
        if (res.ok) {
          const data = await res.json();
          setShahadatEnabled(data.shahadat_mode);
          setShahadatMessage(data.shahadat_message || '');
          setHeroBanner(data.hero_banner || '');
        }
      } catch (err) { logger.error('Failed to load settings:', err); }
      finally { setLoading(false); }
    };
    load();
  }, [apiBaseUrl]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const params = new URLSearchParams({
        enabled: shahadatEnabled,
        message: shahadatMessage,
        hero_banner: heroBanner
      });
      const res = await fetch(
        `${apiBaseUrl}/api/admin/settings/shahadat?${params.toString()}`,
        { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) { logger.error('Failed to save settings:', err); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="p-12 text-center"><Loader2 className="w-8 h-8 text-[#c5a572] animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="bg-white shadow-lg">
        <div className="p-6 border-b">
          <h2 className="text-xl text-[#1a2f23]">Hero Banner Message</h2>
          <p className="text-sm text-[#5a6d62] mt-1">
            A prominent message displayed on the homepage hero section. Use it for announcements, Muharram, Eid, or any occasion. Leave empty to hide the banner.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label className="text-sm text-[#5a6d62]">Banner Text</Label>
            <p className="text-xs text-[#5a6d62] mb-2">
              Supports bilingual text. Examples: "Muharram al-Haram 1448 · محرم الحرام ۱۴۴۸" or "Eid Ghadir Mubarak · عید غدیر مبارک"
            </p>
            <Input
              value={heroBanner}
              onChange={(e) => setHeroBanner(e.target.value)}
              placeholder="e.g. Muharram al-Haram 1448 · محرم الحرام ۱۴۴۸"
              className="mt-1"
              data-testid="hero-banner-input"
            />
          </div>

          {/* Preview */}
          {heroBanner && (
            <div className="border border-dashed border-gray-300 rounded p-4">
              <p className="text-xs text-[#5a6d62] mb-2">Preview — Hero Banner:</p>
              <div className={`text-center py-5 px-6 border ${shahadatEnabled ? 'bg-[#0a0000] border-[#8b3a3a]/30' : 'bg-[#1a2f23] border-[#c5a572]/30'}`}>
                <p className="text-white/90 text-lg tracking-wide font-display">{heroBanner}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Shahadat Mode */}
      <div className="bg-white shadow-lg">
        <div className="p-6 border-b">
          <h2 className="text-xl text-[#1a2f23]">Shahadat / Mourning Mode</h2>
          <p className="text-sm text-[#5a6d62] mt-1">
            Toggle the mourning theme for Muharram, shahadat occasions, or other solemn periods. When enabled, the website displays a mourning strip, muted colors, and a darker tone.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#1a2f23]">Enable Mourning Mode</p>
              <p className="text-xs text-[#5a6d62] mt-1">Activates the dark mourning theme across the entire website</p>
            </div>
            <button
              onClick={() => setShahadatEnabled(!shahadatEnabled)}
              className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${shahadatEnabled ? 'bg-[#5c1a1a]' : 'bg-gray-300'}`}
              data-testid="shahadat-toggle"
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-300 ${shahadatEnabled ? 'translate-x-7' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Mourning Strip Message */}
          <div>
            <Label className="text-sm text-[#5a6d62]">Mourning Strip Message</Label>
            <p className="text-xs text-[#5a6d62] mb-2">
              Displayed at the top of every page when mourning mode is on. Leave empty for the default message.
            </p>
            <Input
              value={shahadatMessage}
              onChange={(e) => setShahadatMessage(e.target.value)}
              placeholder="e.g. Muharram al-Haram 1448 · محرم الحرام ۱۴۴۸"
              className="mt-1"
              data-testid="shahadat-message-input"
            />
          </div>

          {/* Mourning Strip Preview */}
          {shahadatEnabled && (
            <div className="border border-dashed border-[#5c1a1a]/30 rounded p-4">
              <p className="text-xs text-[#5a6d62] mb-2">Preview — Mourning Strip:</p>
              <div className="bg-[#0a0000] text-center py-1.5 border border-[#5c1a1a]/30">
                <p className="text-[#c27a7a]/80 text-[10px] sm:text-xs tracking-[0.3em] uppercase">
                  {shahadatMessage || 'Inna Lillahi Wa Inna Ilayhi Rajioon · إنا لله و إنا إليه راجعون'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center gap-4">
        <Button onClick={save} className="bg-[#1a2f23] hover:bg-[#2a4a38] text-white" disabled={saving} data-testid="settings-save-button">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : 'Save All Settings'}
        </Button>
        {saved && <span className="text-sm text-green-600">Settings saved. Refresh the website to see changes.</span>}
      </div>
    </div>
  );
};

export default SettingsTab;
